import React, { useCallback } from "react";
import { connect } from "react-redux";

import { faLock } from "@fortawesome/pro-solid-svg-icons/faLock";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FormInstance } from "antd";
import Form from "antd/es/form";
import { Col } from "antd/es/grid";
import Row from "antd/es/row";
import Space from "antd/es/space";
import TimePicker from "antd/es/time-picker";
import moment from "moment";
import { useIntl } from "react-intl";

import { TextField } from "@mapmycustomers/ui";

import Checkbox from "@app/component/input/Checkbox";
import OrganizationSetting from "@app/enum/OrganizationSetting";
import TimeZones from "@app/enum/TimeZones";
import useWorkingHoursValidationRules from "@app/scene/settings/component/EmailPreferences/useWorkingHoursValidationRules";
import { getOrganizationSettingValue } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import layout from "@app/styles/layout";
import TimeZone from "@app/types/TimeZone";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import { SHOULD_USE_12_HOUR_FORMAT } from "@app/util/consts";

import styles from "./EmailPreferences.module.scss";
import { EmailPreferencesForm } from "./index";
import messages from "./messages";
import { WEEKEND_DAYS } from "./util/const";

interface Props {
  form: FormInstance<EmailPreferencesForm>;
  timezoneSettingValue: any;
}

const SendingHours: React.FC<Props> = ({ form, timezoneSettingValue }) => {
  const intl = useIntl();
  const analytics = useExtendedAnalytics(["Sending Hours"]);

  const workingHoursValidationRules = useWorkingHoursValidationRules();

  const handleRangeChange = useCallback(
    ([startTime, endTime]) => {
      const from = startTime.format("HH:mm");
      const to = endTime.format("HH:mm");
      form.setFieldValue("workingHours", [from, to]);
      analytics.clicked(["Changed"], { from, to });
    },
    [analytics, form]
  );
  const handleChangeExcludeWeekends = useCallback(
    (exclude: boolean) => {
      form.setFieldsValue({
        excludedDays: exclude ? WEEKEND_DAYS : [],
      });
    },
    [form]
  );

  return (
    <div className={styles.sendingHoursContainer}>
      <div className={styles.settingLabel}>
        {intl.formatMessage(messages.limitSendingQueueTitle)}
      </div>
      <div className={styles.settingSubLabel}>
        {intl.formatMessage(messages.limitSendingQueueDescription)}
      </div>
      <Row align="top" gutter={layout.spacerM}>
        <Col>
          <Form.Item
            className={styles.formItem}
            name="workingHours"
            rules={workingHoursValidationRules}
            valuePropName="fake"
          >
            <TimePicker.RangePicker
              clearIcon={null}
              defaultValue={[
                moment(`2000-01-01 ${form.getFieldValue("workingHours")[0]}`),
                moment(`2000-01-01 ${form.getFieldValue("workingHours")[1]}`),
              ]}
              format={`${SHOULD_USE_12_HOUR_FORMAT ? "hh:mm a" : "HH:mm"}`}
              onChange={handleRangeChange}
              use12Hours={SHOULD_USE_12_HOUR_FORMAT}
            />
          </Form.Item>
        </Col>
        <Col>
          <TextField
            className={styles.timezoneField}
            disabled
            suffix={<FontAwesomeIcon icon={faLock} />}
            value={intl.formatMessage(TimeZones[timezoneSettingValue as TimeZone]) ?? ""}
          />
        </Col>
        <Col>
          <Space>
            <Form.Item className={styles.formItem} name="excludedDays">
              <Checkbox
                defaultChecked={WEEKEND_DAYS.every((day) =>
                  form.getFieldValue("excludedDays").includes(day)
                )}
                onChange={handleChangeExcludeWeekends}
              />
            </Form.Item>
            <span className={styles.excludeWeekends}>
              {intl.formatMessage(messages.limitSendingQueueWeekends)}
            </span>
          </Space>
        </Col>
      </Row>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  timezoneSettingValue: getOrganizationSettingValue(state)(OrganizationSetting.TIMEZONE),
});

export default connect(mapStateToProps)(SendingHours);
